// components/portal/WaitingPulseTimer.tsx
'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, Loader2 } from '@/components/ui/lucide-memo';

interface WaitingPulseTimerProps {
  startAtIso?: string | null;
  durationSeconds?: number;
  title: string;
  subtitle: string;
  timerLabel: string;
  expiredTitle?: string;
  expiredSubtitle?: string;
  className?: string;
  onExpired?: () => void;
}

const formatSeconds = (seconds: number): string => {
  const safe = Math.max(0, seconds);
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export const WaitingPulseTimer = ({
  startAtIso,
  durationSeconds = 45,
  title,
  subtitle,
  timerLabel,
  expiredTitle = 'Warm-up timed out',
  expiredSubtitle = 'The campaign failed to start within the expected time. Please try again or contact support.',
  className,
  onExpired,
}: WaitingPulseTimerProps) => {
  const parsedStartAtMs = useMemo(() => {
    const parsed = Date.parse(startAtIso || '');
    return Number.isFinite(parsed) ? parsed : null;
  }, [startAtIso]);
  const fallbackStartAtMsRef = useRef<number | null>(null);

  const [remainingSeconds, setRemainingSeconds] = useState(durationSeconds);
  const [isExpired, setIsExpired] = useState(false);
  const onExpiredCalledRef = useRef(false);

  useEffect(() => {
    if (fallbackStartAtMsRef.current === null) {
      fallbackStartAtMsRef.current = Date.now();
    }
    const startAtMs = parsedStartAtMs ?? fallbackStartAtMsRef.current;
    const tick = () => {
      const elapsed = Math.max(0, Math.floor((Date.now() - startAtMs) / 1000));
      const remaining = Math.max(0, durationSeconds - elapsed);
      setRemainingSeconds(remaining);
      if (remaining === 0 && !onExpiredCalledRef.current) {
        // Give a 5s grace period after countdown reaches 0
        setTimeout(() => {
          setIsExpired(true);
          if (!onExpiredCalledRef.current) {
            onExpiredCalledRef.current = true;
            onExpired?.();
          }
        }, 5000);
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [parsedStartAtMs, durationSeconds, onExpired]);

  const progress = durationSeconds > 0 ? (durationSeconds - remainingSeconds) / durationSeconds : 1;
  const clampedProgress = Math.max(0, Math.min(1, progress));

  if (isExpired) {
    return (
      <div className={clsx('rounded-xl border border-[#FF7369]/20 bg-[#141414] p-4', className)}>
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#FF7369]/10">
            <AlertTriangle size={16} className="text-[#FF7369]" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-white/90">{expiredTitle}</div>
            <div className="mt-0.5 text-[12px] text-white/40">{expiredSubtitle}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('rounded-xl border border-[#2A2A2A] bg-[#141414] p-4', className)}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-[#B3D9FF]/10">
            <Loader2 size={16} className="animate-spin text-[#B3D9FF]" />
          </div>
          <div className="min-w-0">
            <div className="text-[13px] font-medium text-white/90">{title}</div>
            <div className="mt-0.5 text-[12px] text-white/40">{subtitle}</div>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-[10px] text-white/30">{timerLabel}</div>
          <div className="font-mono text-lg font-semibold tabular-nums text-white/80">
            {formatSeconds(remainingSeconds)}
          </div>
        </div>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-white/5">
        <div
          className="h-full rounded-full bg-[#B3D9FF]/60 transition-[width] duration-700 ease-linear"
          style={{ width: `${clampedProgress * 100}%` }}
        />
      </div>
    </div>
  );
};
